export function createReducer(initialState, reducerMap) {
  return (state, action) => {
    if (!state) {state = initialState;}
    const reducer = reducerMap[action.type];

    return reducer ? reducer(state, action) : state;
  };
}

export function createRequestReducer(state, action, reducerMap) {
  const reducer = reducerMap[action.status];

  return reducer ? reducer(state, action) : state;
}

export function defaultListInitialState() {
  return {
    isFetching: false,
    items: null,
    error: null,

    // query params used to get list
    query: null
  };
}

export function defaultObjectInitialState() {
  return {
    isFetching: false,
    data: null,
    error: null
  };
}

function errorReducer(state, action) {
  return Object.assign({}, state, {
    isFetching: false,
    error: action.error
  });
}

export function defaultGetReducer(state, action) {
  return createRequestReducer(state, action, {
    SEND: (state, action) =>
      Object.assign({}, state, {
        isFetching: true,
        items: null,
        error: null
      }),

    SUCCESS: (state, action) =>
      Object.assign({}, state, {
        isFetching: false,
        error: null,
        items: action.response
      }),

    FAIL: errorReducer
  });
}

export function defaultObjectReducer(state, action) {
  return createRequestReducer(state, action, {
    SEND: (state) =>
      Object.assign({}, state, {
        isFetching: true,
        data: null,
        error: null
      }),

    SUCCESS: (state, action) =>
      Object.assign({}, state, {
        isFetching: false,
        data: action.response
      }),

    FAIL: errorReducer
  });
}

function withEndingSlash(endpoint) {
  const d = endpoint && endpoint[endpoint.length - 1] !== '/' ? '/' : '';
  return `${endpoint}${d}`;
}

// Reducer factory functions
const id = x => x;

/**
 * Create single item fetch reducer that discriminates action by status.
 * * SEND - mark as fetching. Clear prev error. Keep data.
 * * SUCCESS - mark as not fetching. Save action.response. Clear prev error.
 * * FAIL - mark as not fetching. Save action.error. Keep prev item.
 * Also reducer has initialState property that creates initial state ( for those
 * of us lazy to call reducer w/o data)
 * @param {Object} options. Custom names for keys (item = data, error = error, isFetching = isFetching)
 */
export const createSingleItemReducer = ({
  item = 'data',
  error = 'error',
  isFetching = 'isFetching'
} = {}) => {
  const actionByStatus = {
    SEND: (state, action) => ({
      ...state,
      [isFetching]: true,
      [error]: null
    }),

    SUCCESS: (state, action) => ({
      ...state,
      [isFetching]: false,
      [error]: null,
      [item]: action.response
    }),

    FAIL: (state, action) => ({
      ...state,
      [isFetching]: false,
      [error]: action.error
    })
  };

  const createInitialState = () => ({
    [item]: null,
    [error]: null,
    [isFetching]: false
  });

  const reducer = (state = createInitialState(), action = {}) => (actionByStatus[action.status] || id)(state, action);

  Object.defineProperty(reducer, 'initialState', {
    get: createInitialState
  });

  return reducer;
};

// Action creators creators.

/**
 * Creates get list from server action with given type
 * connected to given API handle.
 */
export function defaultGetAction(type, endpoint) {
  const finalEndpoint = withEndingSlash(endpoint);

  return queryParams => ({
    type,
    meta: {
      method: 'GET',
      endpoint: finalEndpoint,
      queryParams
    }
  });
}

/**
 * Creates get by id from server action.
 */
export function defaultGetByIdAction(type, endpoint) {
  const finalEndpoint = withEndingSlash(endpoint);

  return (id, queryParams) => ({
    type,
    meta: {
      method: 'GET',
      endpoint: `${finalEndpoint}${id}/`,
      queryParams
    }
  });
}

export function defaultCreateAction(type, endpoint) {
  const finalEndpoint = withEndingSlash(endpoint);

  return data => ({
    type,
    meta: {
      method: 'POST',
      endpoint: finalEndpoint,
      data
    }
  });
}

export function defaultUpdateAction(type, endpoint, key = 'id') {
  const finalEndpoint = withEndingSlash(endpoint);

  return data => ({
    type,
    meta: {
      method: 'PUT',
      endpoint: `${finalEndpoint}${data[key]}/`,
      data
    }
  });
}

export function defaultDeleteAction(type, endpoint) {
  const finalEndpoint = withEndingSlash(endpoint);

  return id => ({
    type,
    meta: {
      method: 'DELETE',
      endpoint: `${finalEndpoint}${id}/`,
      id
    }
  });
}

export function defaultClearAction(type) {
  return () => ({ type });
}



// WEBPACK FOOTER //
// ./src/utils/redux.js